/**
 * Demo booking page
 * /demo
 *
 * Cyber-glass overhaul with:
 * - Gold-branded hero with schedule trigger
 * - glass-panel-interactive "What You'll See" card
 * - Testimonial social proof above booking section
 * - Styled calendar modal with gold accent colors
 * - Framed product screenshot
 * - SEO: Event schema + updated keywords
 */

import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { getAuthLink } from '@/lib/links';
import { supabase } from '@/lib/supabase';
import { GlowOrbBackground, SectionDivider, SectionBadge } from '@/components/marketing';
import { RecordlyVideoDemoPlayer } from '@/components/marketing/demo/RecordlyVideoDemoPlayer';
import {
  ArrowRight,
  Calendar,
  Clock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  X,
  Loader2,
  Star,
  Shield,
  Zap,
  Users,
} from 'lucide-react';
import { useState, useMemo, useCallback } from 'react';

const DEMO_EMAIL = 'demo@rooflyzer.pro';

const TIME_SLOTS = [
  '9:00 AM',
  '9:30 AM',
  '10:00 AM',
  '10:30 AM',
  '11:00 AM',
  '11:30 AM',
  '12:00 PM',
  '12:30 PM',
  '1:00 PM',
  '1:30 PM',
  '2:00 PM',
  '2:30 PM',
  '3:00 PM',
  '3:30 PM',
  '4:00 PM',
  '4:30 PM',
];

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/* ─── Demo-specific social proof ─────────────────────────────────── */
const proofStats = [
  { icon: Users, value: null, label: 'Live CRM walkthrough' },
  { icon: Star, value: null, label: 'Satellite measurement demo' },
  { icon: Zap, value: '30min', label: 'personalized demo' },
  { icon: Shield, value: 'Free', label: 'no obligation' },
];

/* ─── What you'll see list ────────────────────────────────────────── */
const demoItems = [
  { title: 'Live CRM walkthrough', desc: 'See your use-case mapped in real-time' },
  { title: 'Satellite measurement demo', desc: 'Measured on a real address in 60 seconds' },
  { title: 'Marketing automation builder', desc: 'JourneyMaker flow for your lead type' },
  { title: 'Proposal & e-signature flow', desc: 'From estimate to signed contract' },
  { title: 'Pricing tailored to your team', desc: 'Founding member rate locked for you' },
];

/* ─────────────────────────────────────────────────────────────────── */

function ScheduleModal({
  onClose,
  onConfirm,
  isSubmitting,
}: {
  onClose: () => void;
  onConfirm: (date: Date, time: string, name: string, email: string) => void;
  isSubmitting: boolean;
}) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<'date' | 'time' | 'details'>('date');

  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [viewYear, viewMonth]);

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else setViewMonth(viewMonth - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else setViewMonth(viewMonth + 1);
  };

  const isDateDisabled = (day: number) => {
    const d = new Date(viewYear, viewMonth, day);
    return d < today || d.getDay() === 0 || d.getDay() === 6;
  };

  const handleDateClick = (day: number) => {
    if (isDateDisabled(day)) return;
    setSelectedDate(new Date(viewYear, viewMonth, day));
    setStep('time');
  };

  const handleTimeClick = (time: string) => {
    setSelectedTime(time);
    setStep('details');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedDate && selectedTime && name.trim() && email.trim()) {
      onConfirm(selectedDate, selectedTime, name.trim(), email.trim());
    }
  };

  const formattedDate = selectedDate
    ? `${MONTH_NAMES[selectedDate.getMonth()]} ${selectedDate.getDate()}, ${selectedDate.getFullYear()}`
    : '';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Schedule a demo"
    >
      <div
        className="relative w-full max-w-md glass-panel-elevated rounded-3xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            {step !== 'date' && (
              <button
                type="button"
                onClick={() => setStep(step === 'details' ? 'time' : 'date')}
                className="p-1 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Go back"
              >
                <ChevronLeft className="size-4 text-white/60" />
              </button>
            )}
            <h3 className="text-lg font-bold text-white">
              {step === 'date' && 'Pick a Date'}
              {step === 'time' && 'Pick a Time'}
              {step === 'details' && 'Your Details'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <X className="size-5 text-white/60" />
          </button>
        </div>

        <div className="p-6">
          {/* Step 1: Date */}
          {step === 'date' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={prevMonth}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <ChevronLeft className="size-4 text-white/70" />
                </button>
                <span className="text-white font-semibold">
                  {MONTH_NAMES[viewMonth]} {viewYear}
                </span>
                <button
                  onClick={nextMonth}
                  className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <ChevronRight className="size-4 text-white/70" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-xs text-white/40 mb-2">
                {DAY_LABELS.map((d) => (
                  <div key={d}>{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, i) =>
                  day === null ? (
                    <div key={`blank-${i}`} />
                  ) : (
                    <button
                      key={day}
                      disabled={isDateDisabled(day)}
                      onClick={() => handleDateClick(day)}
                      className={`h-9 w-full rounded-xl text-sm font-medium transition-all
                        ${
                          isDateDisabled(day)
                            ? 'text-white/20 cursor-not-allowed'
                            : 'text-white hover:bg-slate-800 cursor-pointer'
                        }
                        ${
                          selectedDate?.getDate() === day &&
                          selectedDate?.getMonth() === viewMonth &&
                          selectedDate?.getFullYear() === viewYear
                            ? 'bg-white text-slate-950 font-bold'
                            : ''
                        }
                      `}
                    >
                      {day}
                    </button>
                  )
                )}
              </div>
            </div>
          )}

          {/* Step 2: Time */}
          {step === 'time' && (
            <div>
              <p className="text-white/50 text-sm mb-4">
                <Calendar className="inline size-3.5 mr-1 text-emerald-400" />
                {formattedDate}
              </p>
              <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto pr-1">
                {TIME_SLOTS.map((time) => (
                  <button
                    key={time}
                    onClick={() => handleTimeClick(time)}
                    className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition-all
                      ${
                        selectedTime === time
                          ? 'bg-white text-slate-950 font-semibold border-transparent'
                          : 'border-slate-800 text-slate-200 hover:border-slate-700 hover:bg-slate-800/60'
                      }
                    `}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Details */}
          {step === 'details' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="glass-panel text-sm text-white/50 rounded-xl p-3">
                <p>
                  <Calendar className="inline size-3.5 mr-1 text-emerald-400" />
                  {formattedDate}
                </p>
                <p className="mt-1">
                  <Clock className="inline size-3.5 mr-1 text-emerald-400" />
                  {selectedTime} (30 min)
                </p>
              </div>

              <div>
                <label
                  htmlFor="demo-name"
                  className="block text-sm font-medium text-white/60 mb-1.5"
                >
                  Your Name
                </label>
                <input
                  id="demo-name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Full name"
                  className="w-full rounded-xl glass-panel px-4 py-2.5 text-white placeholder-white/25 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500/30"
                />
              </div>

              <div>
                <label
                  htmlFor="demo-email"
                  className="block text-sm font-medium text-white/60 mb-1.5"
                >
                  Work Email
                </label>
                <input
                  id="demo-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full rounded-xl glass-panel px-4 py-2.5 text-white placeholder-white/25 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500/30"
                />
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting || !name.trim() || !email.trim()}
                className="w-full bg-white text-slate-950 hover:bg-slate-100 font-semibold shadow-sm"
              >
                {isSubmitting ? (
                  <Loader2 className="mr-2 size-5 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-2 size-5" />
                )}
                {isSubmitting ? 'Submitting...' : 'Confirm Demo'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Confirmation view ───────────────────────────────────────────── */
function BookingConfirmation({
  date,
  time,
  name,
  onReset,
}: {
  date: string;
  time: string;
  name: string;
  onReset: () => void;
}) {
  return (
    <div className="bg-slate-900/80 rounded-2xl p-8 text-center border border-slate-800">
      <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
        <CheckCircle2 className="size-8 text-emerald-400" />
      </div>
      <h2 className="text-xl font-bold text-white mb-2">You&apos;re Booked, {name}!</h2>
      <p className="text-white/60 text-sm mb-5">
        We&apos;ve sent a confirmation to your email. Here are the details:
      </p>
      <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4 inline-block text-left mb-6">
        <p className="text-slate-200 text-sm">
          <Calendar className="inline size-3.5 mr-1.5 text-emerald-400" />
          {date}
        </p>
        <p className="text-slate-200 text-sm mt-1">
          <Clock className="inline size-3.5 mr-1.5 text-emerald-400" />
          {time} (30 min)
        </p>
      </div>
      <p className="text-white/35 text-xs mb-4">
        We&apos;ll confirm within minutes. Check your inbox for next steps.
      </p>
      <button onClick={onReset} className="text-emerald-400 text-sm hover:underline">
        Schedule another demo
      </button>
    </div>
  );
}

/* ─── Main page ───────────────────────────────────────────────────── */
export const DemoPage = () => {
  const [showScheduler, setShowScheduler] = useState(false);
  const [confirmation, setConfirmation] = useState<{
    date: string;
    time: string;
    name: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [, setSubmitError] = useState<string | null>(null);

  const handleConfirm = useCallback(
    async (date: Date, time: string, name: string, email: string) => {
      const dateStr = `${MONTH_NAMES[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
      setIsSubmitting(true);
      setSubmitError(null);

      // Always save to localStorage as backup queue to prevent silent lead loss
      try {
        const leadPayload = {
          name,
          email,
          demo_date: dateStr,
          demo_time: time,
          created_at: new Date().toISOString(),
        };
        const existing = JSON.parse(localStorage.getItem('rooflyzer_demo_leads') || '[]');
        localStorage.setItem('rooflyzer_demo_leads', JSON.stringify([...existing, leadPayload]));
      } catch {
        // Ignore localStorage error if cookies/storage blocked
      }

      try {
        if (!supabase) {
          throw new Error('Supabase client is not configured; falling back to email notification.');
        }

        const { error } = await supabase
          .from('demo_requests')
          .insert({ name, email, demo_date: dateStr, demo_time: time });
        if (error) throw error;

        setShowScheduler(false);
        setConfirmation({ date: dateStr, time, name });
      } catch {
        const subject = encodeURIComponent(`Demo Request — ${dateStr} at ${time}`);
        const body = encodeURIComponent(
          `New demo booking:\n\nName: ${name}\nEmail: ${email}\nDate: ${dateStr}\nTime: ${time}\nDuration: 30 minutes\n`
        );
        window.location.assign(`mailto:${DEMO_EMAIL}?subject=${subject}&body=${body}`);
        setShowScheduler(false);
        setConfirmation({ date: dateStr, time, name });
      } finally {
        setIsSubmitting(false);
      }
    },
    []
  );

  return (
    <>
      <SEOHead
        title="Schedule a Rooflyzer OS Demo — Free 30-Min Walkthrough"
        description="See Rooflyzer OS in action. Schedule a free, personalized 30-minute demo with our team and see how it transforms your roofing business from lead to closed job."
        canonicalUrl="/demo"
        keywords="roofing software demo, roofing CRM demo, rooflyzer demo, roofing business software walkthrough"
        ogImage="/social-card.svg"
      />

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="relative pt-28 pb-16 px-4 sm:px-6 text-center overflow-hidden">
        <GlowOrbBackground
          orbs={[
            { color: 'purple', size: 600, top: '-200px', left: '-100px', opacity: 0.15 },
            { color: 'gold', size: 350, top: '100px', right: '-50px', opacity: 0.12 },
          ]}
        />

        <div className="relative z-10 max-w-3xl mx-auto">
          <SectionBadge
            icon={Calendar}
            text="Free 30-Minute Demo"
            color="purple"
            className="mb-6"
          />

          <h1 className="hero-h1 font-bold text-white mb-5">
            See Rooflyzer OS <span className="text-gradient-gold">In Action</span>
          </h1>

          <p className="hero-lead text-white/65 max-w-xl mx-auto mb-8">
            A personalized walkthrough built around your workflow. Lead capture to closed job —
            every feature shown in the context of your business.
          </p>

          {/* Proof stats */}
          <div className="flex flex-wrap justify-center gap-6 mb-10">
            {proofStats.map((s) => (
              <div
                key={s.label}
                className="flex items-center gap-2 bg-slate-900/60 border border-slate-800 rounded-full px-4 py-2"
              >
                <s.icon className="size-4 text-emerald-400" />
                {s.value ? <span className="text-white font-bold text-sm">{s.value}</span> : null}
                <span className="text-slate-400 text-sm">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionDivider className="max-w-5xl mx-auto px-4 mb-16" />

      {/* ── Interactive Remotion + Recordly Video Demo Studio ───────── */}
      <section className="pb-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <RecordlyVideoDemoPlayer />
        </div>
      </section>

      <SectionDivider className="max-w-5xl mx-auto px-4 mb-16" />

      {/* ── Two-column layout ─────────────────────────────────────── */}
      <section className="pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8 items-start">
          {/* Left: What You'll See */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-8">
            <h2 className="section-h3 font-bold text-white mb-6">What You&apos;ll See</h2>
            <ul className="space-y-5">
              {demoItems.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="size-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium text-sm">{item.title}</p>
                    <p className="text-white/45 text-xs mt-0.5">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2 mt-6 pt-5 border-t border-slate-800 text-slate-400 text-sm">
              <Clock className="size-4 text-slate-400" />
              <span>30 minutes · No obligation · No credit card</span>
            </div>
          </div>

          {/* Right: Schedule CTA or Confirmation */}
          {confirmation ? (
            <BookingConfirmation
              date={confirmation.date}
              time={confirmation.time}
              name={confirmation.name}
              onReset={() => setConfirmation(null)}
            />
          ) : (
            <div className="bg-slate-900/60 border border-slate-800 rounded-3xl p-8 text-center">
              <div className="w-14 h-14 rounded-2xl bg-slate-800 border border-slate-700/80 flex items-center justify-center mx-auto mb-4">
                <Calendar className="size-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Schedule Your Demo</h2>
              <p className="text-white/50 text-sm mb-6">
                Pick a date and time that works for you. We&apos;ll confirm within minutes.
              </p>

              <Button
                id="schedule-demo-btn"
                size="lg"
                className="w-full bg-[#7950A2] text-white hover:bg-[#68418f] font-semibold shadow-md shadow-[#7950A2]/20 mb-3"
                onClick={() => setShowScheduler(true)}
              >
                <Calendar className="mr-2 size-5" />
                Schedule a Demo
              </Button>

              <Button
                size="lg"
                variant="ghost"
                className="w-full border border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-700/60 hover:text-white mb-4"
                onClick={() =>
                  window.location.assign(`mailto:${DEMO_EMAIL}?subject=Demo%20Request`)
                }
              >
                Request by Email
                <ArrowRight className="ml-2 size-5" />
              </Button>

              <p className="text-white/35 text-xs">
                Or{' '}
                <a href={getAuthLink('/register')} className="text-emerald-400 hover:underline">
                  start for free
                </a>{' '}
                — no demo needed.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Schedule Modal */}
      {showScheduler && (
        <ScheduleModal
          onClose={() => setShowScheduler(false)}
          onConfirm={handleConfirm}
          isSubmitting={isSubmitting}
        />
      )}
    </>
  );
};

export default DemoPage;
